/**
 * QUIPWA — reading and writing the replicated part of `_state.db`
 *
 * `exportReplicatedState` reads every replicated table into plain rows;
 * `applyReplicationChanges` writes a diff back in one transaction. The merge
 * itself (state-merge.ts) never touches a database, which is what makes it
 * testable as a pure function.
 */

import type { IDBAdapter, SQLParam } from '../db/db-adapter.ts'
import {
  DECK_INSTANCES, DECK_TAGS, REVIEW_LOGS, REVIEW_STATES, SESSIONS, SESSION_DECKS, SETTINGS, TAGS, TOMBSTONES,
  SOCRATIC_ATTEMPTS, SOCRATIC_EVENTS, SOCRATIC_ITEMS,
  type ReplicatedState, type TableSpec,
} from './state-tables.ts'
import type { ReplicationChanges, TableChanges } from './state-merge.ts'

async function readTable<Row>(db: IDBAdapter, table: TableSpec<Row>): Promise<Row[]> {
  return db.query<Row>(`SELECT ${table.columns.join(', ')} FROM ${table.table}`)
}

export async function exportReplicatedState(db: IDBAdapter): Promise<ReplicatedState> {
  return {
    settings: await readTable(db, SETTINGS),
    deckInstances: await readTable(db, DECK_INSTANCES),
    tags: await readTable(db, TAGS),
    deckTags: await readTable(db, DECK_TAGS),
    sessions: await readTable(db, SESSIONS),
    sessionDecks: await readTable(db, SESSION_DECKS),
    reviewStates: await readTable(db, REVIEW_STATES),
    reviewLogs: await readTable(db, REVIEW_LOGS),
    socraticItems: await readTable(db, SOCRATIC_ITEMS),
    socraticAttempts: await readTable(db, SOCRATIC_ATTEMPTS),
    socraticEvents: await readTable(db, SOCRATIC_EVENTS),
    tombstones: await readTable(db, TOMBSTONES),
  }
}

function values<Row>(row: Row, columns: readonly (keyof Row & string)[]): SQLParam[] {
  return columns.map((c) => (row[c] ?? null) as SQLParam)
}

async function applyTable<Row>(
  db: IDBAdapter,
  table: TableSpec<Row>,
  changes: TableChanges<Row>,
  insertVerb: 'INSERT OR REPLACE' | 'INSERT OR IGNORE' = 'INSERT OR REPLACE'
): Promise<void> {
  const where = table.keyColumns.map((c) => `${c} = ?`).join(' AND ')
  for (const row of changes.deletes) {
    await db.run(`DELETE FROM ${table.table} WHERE ${where}`, values(row, table.keyColumns))
  }
  const placeholders = table.columns.map(() => '?').join(', ')
  const sql = `${insertVerb} INTO ${table.table} (${table.columns.join(', ')}) VALUES (${placeholders})`
  for (const row of changes.upserts) {
    await db.run(sql, values(row, table.columns))
  }
}

/** Write a merge result's local changes atomically. */
export async function applyReplicationChanges(db: IDBAdapter, changes: ReplicationChanges): Promise<void> {
  await db.transaction(async (tx) => {
    // Children before parents on delete, parents before children on insert —
    // irrelevant while foreign keys are off, but it keeps the intent readable.
    await applyTable(tx, TOMBSTONES, changes.tombstones)
    await applyTable(tx, REVIEW_LOGS, changes.reviewLogs, 'INSERT OR IGNORE')
    await applyTable(tx, REVIEW_STATES, changes.reviewStates)
    await applyTable(tx, SESSION_DECKS, changes.sessionDecks)
    await applyTable(tx, DECK_TAGS, changes.deckTags)
    await applyTable(tx, DECK_INSTANCES, changes.deckInstances)
    await applyTable(tx, TAGS, changes.tags)
    await applyTable(tx, SESSIONS, changes.sessions)
    await applyTable(tx, SETTINGS, changes.settings)
    await applyTable(tx, SOCRATIC_ITEMS, changes.socraticItems)
    await applyTable(tx, SOCRATIC_ATTEMPTS, changes.socraticAttempts, 'INSERT OR IGNORE')
    await applyTable(tx, SOCRATIC_EVENTS, changes.socraticEvents, 'INSERT OR IGNORE')
  })
}
